import type {
    Content,
    ContentIconFields,
    ContentImageFields,
    ContentTileFields,
} from '@beat-venues/common-types';
import { urlUtil } from '@beat-venues/utils';
import { useRef, type JSX } from 'react';

import { Tile } from '../../molecules';
import type { CommonProps } from '../../types';
import { useDraggableScroll } from '../../useDraggableScroll';
import { HeadingLayout } from '../heading-layout';
import { tv, type VariantProps } from 'tailwind-variants';

export interface TileCollectionProps
    extends CommonProps,
        VariantProps<typeof TileCollectionStyles> {
    showDescription?: boolean;
    style: 'lg' | 'sm' | 'round';
    title?: string;
    description?: string;
    contentTiles: Content<'contentTile', ContentTileFields>[];
    invertFontColour: boolean;
    locale: string;
    tileCta?: { label: string; href: string };
    centerTitle?: boolean;
}

export const TileCollectionStyles = tv({
    variants: {
        renderMode: {
            large: 'tile-overflow-grid auto-cols-[calc((100%-0.5rem)/2)] md:auto-cols-[calc((100%-4rem)/4)] lg:auto-cols-[calc((100%-3rem)/4)]',
            largeInRTE:
                'tile-overflow-grid auto-cols-[calc((100%-0.5rem)/2)] md:auto-cols-[calc((100%-4rem)/2)] lg:auto-cols-[calc((100%-3rem)/3)]',
            largeDescription:
                'grid gap-2 gap-y-4 md:gap-4 sm:grid-cols-2 lg:grid-cols-4',
            lgThreeCols:
                'grid gap-2 gap-y-4 md:gap-4 sm:grid-cols-2 lg:grid-cols-3',
            largeDescriptionInRTE: 'grid gap-2 gap-y-4 md:gap-4 sm:grid-cols-2',
            smallPlus6:
                'tile-overflow-grid auto-cols-[calc((100%-0.5rem)/2)] xl:auto-cols-[calc((100%-3rem)/6)]',
            small: 'tile-overflow-grid auto-cols-[calc((100%-0.5rem)/2)] md:auto-cols-[calc((100%-3rem)/3)] lg:auto-cols-[calc((100%-4rem)/4)] xl:auto-cols-[calc((100%-3rem)/6)] lg:mx-0 lg:px-0 lg:overflow-visible',
            smallInRTE:
                'tile-overflow-grid auto-cols-[calc((100%-0.5rem)/2)] md:auto-cols-[calc((100%-3rem)/3)] lg:auto-cols-[calc((100%-4rem)/3)]',
            smallInRTEPlus6:
                'tile-overflow-grid auto-cols-[calc((100%-0.5rem)/2)]',
        },
    },
    defaultVariants: {
        renderMode: 'large',
    },
});

export const TileCollection = ({
    className,
    testId = 'tile-collection',
    title,
    description,
    invertFontColour,
    style,
    contentTiles,
    renderMode = 'large',
    showDescription = false,
    locale,
    tileCta,
    centerTitle,
    ...props
}: TileCollectionProps): JSX.Element => {
    const currentLocale =
        locale ?? process.env['NEXT_PUBLIC_DEFAULT_LOCALE'] ?? 'nl';

    const isImage = (
        image:
            | Content<'contentImage', ContentImageFields>
            | Content<'contentIcon', ContentIconFields>
    ): image is Content<'contentImage', ContentImageFields> => {
        return 'altText' in (image.fields as ContentImageFields);
    };

    const scrollRef = useRef<HTMLDivElement>(null);
    const excludedRenderModes = [
        'largeDescription',
        'largeDescriptionInRTE',
        'lgThreeCols',
    ];
    const { mouseDownHandler } = useDraggableScroll(
        scrollRef.current,
        !renderMode || !excludedRenderModes.includes(renderMode)
    );

    const getLinkProps = (tile: Content<'contentTile', ContentTileFields>) => {
        if (!tile.fields.cta || !tile.fields.cta.fields) {
            return {
                href: '',
            };
        }

        if (tile.fields.cta.fields.externalUrl) {
            return {
                href: tile.fields.cta.fields.externalUrl,
            };
        }

        if (!tile.fields.cta.fields.internalEntry)
            return {
                href: '',
            };

        return urlUtil.resolveLink(
            tile.fields.cta.fields.internalEntry,
            tile.fields.cta.fields.internalEntry.sys.locale || currentLocale,
            currentLocale
        );
    };

    return (
        <div {...props} data-testid={testId}>
            <HeadingLayout
                className={centerTitle ? 'mb-0 md:mb-0' : undefined}
                description={description}
                title={title}
                headingClassName={centerTitle ? 'text-center' : undefined}
            >
                <div
                    ref={scrollRef}
                    className={TileCollectionStyles({
                        className: `style-${renderMode} ${className ?? ''} `,
                        renderMode,
                    })}
                    onMouseDown={mouseDownHandler}
                >
                    {contentTiles?.map((tile, index) => (
                        <Tile
                            key={`${index}_${tile.sys.id}`}
                            description={
                                showDescription
                                    ? tile.fields.description
                                    : undefined
                            }
                            icon={
                                tile.fields.image &&
                                tile.fields.image.fields &&
                                (!isImage(tile.fields.image)
                                    ? (
                                          tile.fields.image as Content<
                                              'contentIcon',
                                              ContentIconFields
                                          >
                                      ).fields.iconName
                                    : undefined)
                            }
                            image={
                                tile.fields.image &&
                                tile.fields.image.fields &&
                                (isImage(tile.fields.image)
                                    ? (tile.fields.image as Content<
                                          'contentImage',
                                          ContentImageFields
                                      >)
                                    : undefined)
                            }
                            invertFontColor={invertFontColour}
                            link={tileCta ? undefined : getLinkProps(tile)}
                            locale={locale}
                            size={style}
                            title={tile.fields.title}
                            cta={
                                tileCta
                                    ? {
                                          label: tileCta.label,
                                          href: tileCta.href,
                                          onClick: () => {
                                              const tier = (
                                                  tile.fields.name
                                                      ?.split(' - ')
                                                      .pop() ??
                                                  tile.fields.title
                                              ).toUpperCase();
                                              sessionStorage.setItem(
                                                  'clubEncoreTier',
                                                  tier
                                              );
                                              window.dispatchEvent(
                                                  new CustomEvent(
                                                      'club-encore-tier',
                                                      { detail: tier }
                                                  )
                                              );
                                          },
                                      }
                                    : undefined
                            }
                        />
                    ))}
                </div>
            </HeadingLayout>
        </div>
    );
};
